import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Copy, Users, TrendingUp, Share2, Plus, Pause, X, BarChart3, DollarSign, Clock, Eye, Layers, Activity, UserCheck, Rocket, Lightbulb, Shield, Megaphone } from 'lucide-react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { fadeInUp, staggerContainer, tapScale, cardReveal } from '@/lib/motion'
import { Drawer } from 'vaul'

import { useState } from 'react'

const mockActiveRooms = [
  { id: 'R001', stakes: '1/2', players: 5, maxPlayers: 9, spectators: 8, commission: 2, status: 'active', handsDealt: 142, earnings: 3520 },
  { id: 'R002', stakes: '5/10', players: 3, maxPlayers: 6, spectators: 12, commission: 1.5, status: 'active', handsDealt: 87, earnings: 8900 },
  { id: 'R003', stakes: '1/2', players: 0, maxPlayers: 9, spectators: 0, commission: 2, status: 'empty', handsDealt: 0, earnings: 0 },
]

const mockChartData = [
  { day: '一', amount: 120 },
  { day: '二', amount: 85 },
  { day: '三', amount: 210 },
  { day: '四', amount: 156 },
  { day: '五', amount: 92 },
  { day: '六', amount: 178 },
  { day: '日', amount: 145 },
]

const mockCommissionHistory = [
  { date: '今天', platform: 5, agent: 2, total: 7, earnings: 45000, hands: 142 },
  { date: '昨天', platform: 5, agent: 2, total: 7, earnings: 38000, hands: 126 },
  { date: '前天', platform: 5, agent: 1.5, total: 6.5, earnings: 52000, hands: 187 },
  { date: '3天前', platform: 5, agent: 2, total: 7, earnings: 41000, hands: 138 },
  { date: '4天前', platform: 5, agent: 2, total: 7, earnings: 29000, hands: 95 },
]

export function AgentDashboardScreen() {
  const navigate = useNavigate()
  const [copied, setCopied] = useState(false)
  const [closeRoomId, setCloseRoomId] = useState<string | null>(null)
  const [pauseConfirmRoom, setPauseConfirmRoom] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'rooms' | 'history'>('overview')
  const [showNewAgentDemo, setShowNewAgentDemo] = useState(false)
  const referralCode = 'PWG888'

  const handleCopy = () => {
    navigator.clipboard.writeText(referralCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full bg-background"
    >
      {/* Header */}
      <motion.header variants={fadeInUp} className="flex-shrink-0 flex items-center gap-4 px-6 py-4 safe-area-top sticky top-0 bg-background/80 backdrop-blur-xl border-b border-foreground/5 z-40">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full glass-card active:scale-95 transition-all"
        >
          <ArrowLeft className="w-5 h-5 text-foreground" />
        </button>
        <h1 className="text-xl font-display font-semibold text-foreground">代理儀表板</h1>
        <div className="ml-auto">
          <motion.button
            whileTap={tapScale}
            onClick={() => navigate('/create-room')}
            className="flex items-center gap-1.5 px-4 py-2 btn-primary rounded-full text-sm font-bold"
          >
            <Plus className="w-4 h-4" />
            創建
          </motion.button>
        </div>
      </motion.header>

      {/* Tabs */}
      <div className="flex gap-1 px-6 py-3 border-b border-foreground/5">
        {[
          { id: 'overview' as const, label: '總覽' },
          { id: 'rooms' as const, label: '房間管理' },
          { id: 'history' as const, label: '佣金紀錄' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "flex-1 py-2.5 rounded-xl text-xs font-bold transition-all",
              activeTab === tab.id
                ? "bg-gold/20 text-gold border border-gold/30"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {tab.label}
          </button>
        ))}
        <button
          onClick={() => setShowNewAgentDemo(!showNewAgentDemo)}
          className={cn(
            "px-3 py-2.5 rounded-xl text-[10px] font-bold transition-all border",
            showNewAgentDemo
              ? "bg-purple-500/20 text-purple-400 border-purple-500/30"
              : "text-muted-foreground border-transparent hover:text-foreground"
          )}
        >
          新手
        </button>
      </div>

      <motion.main
        variants={staggerContainer}
        className="flex-1 overflow-y-auto px-6 py-6 scrollbar-hide space-y-6 pb-24"
      >
        {/* ============ NEW AGENT EMPTY STATE ============ */}
        {showNewAgentDemo ? (
          <motion.div variants={fadeInUp} className="flex flex-col items-center justify-center py-12 space-y-6">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              className="w-24 h-24 rounded-full bg-gold/10 flex items-center justify-center border border-gold/20"
            >
              <Rocket className="w-12 h-12 text-gold" />
            </motion.div>
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-display font-bold text-foreground">歡迎成為代理！</h2>
              <p className="text-sm text-muted-foreground max-w-[260px]">創建您的第一個房間開始賺取佣金</p>
            </div>
            <motion.button
              whileTap={tapScale}
              onClick={() => navigate('/create-room')}
              className="px-8 py-3.5 btn-primary rounded-2xl font-bold text-base flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              創建房間
            </motion.button>

            {/* Quick Tips */}
            <div className="w-full space-y-3 mt-4">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">快速上手</p>
              {[
                { icon: Lightbulb, title: '設定佣金', desc: '創建房間時可自訂佣金比例，最高 2.5%', color: 'text-gold bg-gold/10' },
                { icon: Megaphone, title: '分享推廣碼', desc: '邀請玩家加入你的房間，持續賺取佣金', color: 'text-blue-400 bg-blue-500/10' },
                { icon: Shield, title: '即時監控', desc: '隨時查看房間狀態、玩家活動和收益數據', color: 'text-emerald-400 bg-emerald-500/10' },
              ].map((tip) => (
                <div key={tip.title} className="flex items-start gap-3 p-4 rounded-2xl glass-card">
                  <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0", tip.color)}>
                    <tip.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground">{tip.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{tip.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ) : (
        <>
        {/* ============ OVERVIEW TAB ============ */}
        {activeTab === 'overview' && (
          <>
            {/* Commission Card - Gold Theme */}
            <motion.section variants={fadeInUp} className="p-6 rounded-3xl glass-card-elevated border-gold/20 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-xs text-gold/80 font-bold uppercase tracking-wider mb-1">本月佣金收益</p>
                    <h2 className="text-3xl font-bold text-foreground tracking-tight font-mono">$8,450.00</h2>
                  </div>
                  <div className="bg-gold text-background px-2 py-1 rounded-lg text-xs font-bold shadow-glow-gold">
                    Lv.3 代理
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="p-3 rounded-2xl bg-foreground/10 border border-foreground/5">
                    <p className="text-xs text-muted-foreground mb-1">昨日收益</p>
                    <p className="text-lg font-bold text-foreground font-mono">$450.00</p>
                  </div>
                  <div className="p-3 rounded-2xl bg-foreground/10 border border-foreground/5">
                    <p className="text-xs text-muted-foreground mb-1">可提領</p>
                    <p className="text-lg font-bold text-gold font-mono">$2,100.00</p>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Share Code */}
            <motion.section variants={fadeInUp} className="p-4 rounded-2xl glass-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-foreground">推廣連結</h3>
                <span className="text-xs text-emerald-400 font-medium">45% 分潤</span>
              </div>
              <div className="flex gap-2">
                <div className="flex-1 h-12 bg-surface-elevated border border-foreground/10 rounded-xl flex items-center justify-between px-4">
                  <span className="font-mono text-foreground/80">{referralCode}</span>
                  <button onClick={handleCopy} className="text-gold hover:text-gold/80">
                    {copied ? <span className="text-xs font-bold text-emerald-400">已複製</span> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <button className="h-12 w-12 glass-card rounded-xl flex items-center justify-center text-foreground">
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
            </motion.section>

            {/* Stats Grid */}
            <motion.section variants={fadeInUp} className="grid grid-cols-2 gap-3">
              {[
                { icon: Users, label: '活躍下線', value: '128', color: 'text-blue-400 bg-blue-500/10' },
                { icon: TrendingUp, label: '總流水', value: '$450k', color: 'text-emerald-400 bg-emerald-500/10' },
                { icon: DollarSign, label: '總佣金', value: '$28.5k', color: 'text-gold bg-gold/10' },
                { icon: BarChart3, label: '活躍房間', value: '2/3', color: 'text-purple-400 bg-purple-500/10' },
              ].map((stat) => (
                <div key={stat.label} className="p-4 rounded-2xl glass-card space-y-2">
                  <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", stat.color)}>
                    <stat.icon className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-2xl font-bold text-foreground font-mono">{stat.value}</span>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              ))}
            </motion.section>

            {/* Recent Activity */}
            <motion.section variants={fadeInUp} className="space-y-3">
              <div className="flex items-center justify-between px-1">
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">下線動態</h3>
                <button className="text-xs text-gold font-bold">查看全部</button>
              </div>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl glass-card">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-surface-elevated flex items-center justify-center text-lg">🤠</div>
                    <div>
                      <p className="text-sm font-bold text-foreground">Player_{8800 + i}</p>
                      <p className="text-xs text-muted-foreground">剛剛在 5/10 級別</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold font-mono text-emerald-400">+$24.50</p>
                    <p className="text-[10px] text-muted-foreground">貢獻佣金</p>
                  </div>
                </div>
              ))}
            </motion.section>

            {/* Active Rooms List */}
            <motion.section variants={fadeInUp} className="space-y-3">
              <div className="flex items-center justify-between px-1">
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                  <Layers className="w-3.5 h-3.5" />
                  活躍房間
                </h3>
                <span className="text-xs text-muted-foreground font-mono">2/3 活躍</span>
              </div>
              {[
                { id: 'R001', name: '高手俱樂部', stakes: '1/2', players: 5, maxPlayers: 9, duration: '2h 35m', status: 'active' as const },
                { id: 'R002', name: 'VIP 牌局', stakes: '5/10', players: 3, maxPlayers: 6, duration: '1h 12m', status: 'active' as const },
                { id: 'R003', name: '新手友好場', stakes: '1/2', players: 0, maxPlayers: 9, duration: '-', status: 'waiting' as const },
              ].map((room) => (
                <div key={room.id} className="p-4 rounded-2xl glass-card">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-surface-elevated flex flex-col items-center justify-center border border-foreground/5">
                        <span className="text-xs font-bold text-gradient-gold">${room.stakes}</span>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground">{room.name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{room.players}/{room.maxPlayers}</span>
                          <span className="w-1 h-1 rounded-full bg-border" />
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{room.duration}</span>
                        </div>
                      </div>
                    </div>
                    <div className={cn(
                      "px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase border",
                      room.status === 'active'
                        ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                        : "bg-amber-500/10 text-amber-400 border-amber-500/20"
                    )}>
                      {room.status === 'active' ? '活躍' : '等待中'}
                    </div>
                  </div>
                </div>
              ))}
            </motion.section>

            {/* Commission Breakdown */}
            <motion.section variants={fadeInUp} className="space-y-3">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1 flex items-center gap-2">
                <DollarSign className="w-3.5 h-3.5" />
                佣金結構明細
              </h3>
              <div className="p-4 rounded-2xl glass-card space-y-3">
                {/* Header row */}
                <div className="grid grid-cols-4 gap-2 pb-2 border-b border-foreground/5">
                  <span className="text-[10px] text-muted-foreground uppercase font-bold">房間</span>
                  <span className="text-[10px] text-muted-foreground uppercase font-bold text-center">平台抽水</span>
                  <span className="text-[10px] text-muted-foreground uppercase font-bold text-center">代理佣金</span>
                  <span className="text-[10px] text-muted-foreground uppercase font-bold text-right">總計</span>
                </div>
                {[
                  { room: 'R001', rake: 5, agent: 2, total: 7 },
                  { room: 'R002', rake: 5, agent: 1.5, total: 6.5 },
                  { room: 'R003', rake: 5, agent: 2, total: 7 },
                ].map((row) => (
                  <div key={row.room} className="grid grid-cols-4 gap-2 items-center">
                    <span className="text-xs font-mono text-foreground">#{row.room}</span>
                    <span className="text-xs font-mono font-bold text-foreground text-center">{row.rake}%</span>
                    <span className="text-xs font-mono font-bold text-gold text-center">{row.agent}%</span>
                    <span className="text-xs font-mono font-bold text-emerald-400 text-right">{row.total}%</span>
                  </div>
                ))}
                {/* Summary */}
                <div className="pt-2 border-t border-foreground/5">
                  <div className="flex justify-between items-center p-3 rounded-xl bg-gold/5 border border-gold/20">
                    <span className="text-xs font-semibold text-gold">你的分潤比例</span>
                    <span className="text-sm font-mono font-bold text-gold">45%</span>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Room Analytics */}
            <motion.section variants={fadeInUp} className="space-y-3">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1 flex items-center gap-2">
                <Activity className="w-3.5 h-3.5" />
                房間分析
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-4 rounded-2xl glass-card text-center">
                  <div className="w-9 h-9 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-2">
                    <Layers className="w-4 h-4 text-blue-400" />
                  </div>
                  <p className="text-xl font-bold text-foreground font-mono">229</p>
                  <p className="text-[10px] text-muted-foreground mt-1">今日發牌數</p>
                </div>
                <div className="p-4 rounded-2xl glass-card text-center">
                  <div className="w-9 h-9 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-2">
                    <UserCheck className="w-4 h-4 text-emerald-400" />
                  </div>
                  <p className="text-xl font-bold text-foreground font-mono">4.2</p>
                  <p className="text-[10px] text-muted-foreground mt-1">平均玩家/房</p>
                </div>
                <div className="p-4 rounded-2xl glass-card text-center">
                  <div className="w-9 h-9 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-2">
                    <TrendingUp className="w-4 h-4 text-purple-400" />
                  </div>
                  <p className="text-xl font-bold text-foreground font-mono">8</p>
                  <p className="text-[10px] text-muted-foreground mt-1">峰值同時在線</p>
                </div>
              </div>
            </motion.section>
          </>
        )}

        {/* ============ ROOMS TAB ============ */}
        {activeTab === 'rooms' && (
          <>
            <motion.section variants={fadeInUp} className="space-y-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold px-1">我的房間 ({mockActiveRooms.length}/3)</p>
              {mockActiveRooms.map((room) => (
                <motion.div key={room.id} variants={cardReveal} className="p-4 rounded-2xl glass-card space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-surface-elevated flex flex-col items-center justify-center border border-foreground/5">
                        <span className="text-[10px] text-muted-foreground">盲注</span>
                        <span className="text-base font-bold text-gradient-gold">${room.stakes}</span>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground">房間 #{room.id}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{room.players}/{room.maxPlayers}</span>
                          <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{room.spectators}</span>
                        </div>
                      </div>
                    </div>
                    <div className={cn(
                      "px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase border",
                      room.status === 'active'
                        ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                        : "bg-surface-elevated text-muted-foreground border-foreground/5"
                    )}>
                      {room.status === 'active' ? '活躍' : '空房'}
                    </div>
                  </div>

                  {/* Room Stats */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2.5 rounded-xl bg-surface-elevated text-center">
                      <p className="text-[10px] text-muted-foreground">佣金</p>
                      <p className="text-sm font-bold text-gold font-mono">{room.commission}%</p>
                    </div>
                    <div className="p-2.5 rounded-xl bg-surface-elevated text-center">
                      <p className="text-[10px] text-muted-foreground">發牌數</p>
                      <p className="text-sm font-bold text-foreground font-mono">{room.handsDealt}</p>
                    </div>
                    <div className="p-2.5 rounded-xl bg-surface-elevated text-center">
                      <p className="text-[10px] text-muted-foreground">收益</p>
                      <p className="text-sm font-bold text-emerald-400 font-mono">${(room.earnings / 100).toFixed(0)}</p>
                    </div>
                  </div>

                  {/* Room Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/room/${room.id}`)}
                      className="flex-1 py-2.5 glass-card rounded-xl text-xs font-semibold text-foreground flex items-center justify-center gap-1.5"
                    >
                      <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                      觀看
                    </button>
                    <button
                      onClick={() => setPauseConfirmRoom(room.id)}
                      className="flex-1 py-2.5 glass-card rounded-xl text-xs font-semibold text-amber-400 flex items-center justify-center gap-1.5"
                    >
                      <Pause className="w-3.5 h-3.5" />
                      暫停
                    </button>
                    <button
                      onClick={() => setCloseRoomId(room.id)}
                      className="flex-1 py-2.5 glass-card rounded-xl text-xs font-semibold text-rose-400 flex items-center justify-center gap-1.5"
                    >
                      <X className="w-3.5 h-3.5" />
                      關閉
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.section>
          </>
        )}

        {/* ============ HISTORY TAB ============ */}
        {activeTab === 'history' && (
          <>
            {/* Commission Chart - 7 Day Bar */}
            <motion.section variants={fadeInUp} className="p-4 rounded-2xl glass-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-foreground">近 7 日佣金</h3>
                <span className="text-xs text-muted-foreground font-mono">
                  ${(mockChartData.reduce((s, d) => s + d.amount, 0) / 100).toFixed(2)}
                </span>
              </div>
              <div className="flex items-end justify-between gap-2 h-28 mb-3">
                {(() => {
                  const maxAmount = Math.max(...mockChartData.map(d => d.amount))
                  return mockChartData.map((d, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[9px] font-mono text-gold/80 font-bold">${(d.amount / 100).toFixed(1)}</span>
                      <div
                        className={cn('w-full rounded-t-md transition-all', i === mockChartData.length - 1 ? 'bg-gold' : 'bg-gold/30')}
                        style={{ height: `${(d.amount / maxAmount) * 100}%` }}
                      />
                      <span className="text-[9px] text-muted-foreground">{d.day}</span>
                    </div>
                  ))
                })()}
              </div>
              <div className="flex items-center justify-between border-t border-foreground/10 pt-3">
                <span className="text-xs text-muted-foreground">7 日總計</span>
                <span className="text-lg font-bold font-mono text-gradient-gold">${(mockChartData.reduce((s, d) => s + d.amount, 0) / 100).toFixed(2)}</span>
              </div>
            </motion.section>

            {/* Commission Breakdown */}
            <motion.section variants={fadeInUp} className="p-4 rounded-2xl glass-card">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold mb-3">佣金明細說明</p>
              <div className="space-y-2">
                <div className="flex justify-between p-3 rounded-xl bg-surface-elevated">
                  <span className="text-sm text-muted-foreground">平台抽水</span>
                  <span className="text-sm font-mono font-bold text-foreground">5%</span>
                </div>
                <div className="flex justify-between p-3 rounded-xl bg-surface-elevated">
                  <span className="text-sm text-muted-foreground">你的佣金</span>
                  <span className="text-sm font-mono font-bold text-gold">1.5-2%</span>
                </div>
                <div className="flex justify-between p-3 rounded-xl bg-gold/5 border border-gold/20">
                  <span className="text-sm font-semibold text-gold">你的分潤比例</span>
                  <span className="text-sm font-mono font-bold text-gold">45%</span>
                </div>
              </div>
            </motion.section>

            {/* Daily History */}
            <motion.section variants={fadeInUp} className="space-y-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold px-1">每日紀錄</p>
              {mockCommissionHistory.map((day, i) => (
                <motion.div key={i} variants={cardReveal} className="p-4 rounded-2xl glass-card">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-semibold text-foreground">{day.date}</span>
                    </div>
                    <span className="text-lg font-mono font-bold text-gold">${(day.earnings / 100).toFixed(0)}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2 rounded-lg bg-surface-elevated text-center">
                      <p className="text-[10px] text-muted-foreground">總抽水</p>
                      <p className="text-xs font-bold text-foreground">{day.total}%</p>
                    </div>
                    <div className="p-2 rounded-lg bg-surface-elevated text-center">
                      <p className="text-[10px] text-muted-foreground">發牌</p>
                      <p className="text-xs font-bold text-foreground">{day.hands}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-surface-elevated text-center">
                      <p className="text-[10px] text-muted-foreground">你的佣金</p>
                      <p className="text-xs font-bold text-gold">{day.agent}%</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.section>
          </>
        )}
        </>
        )}
      </motion.main>

      {/* Close Room Confirmation */}
      <Drawer.Root open={closeRoomId !== null} onOpenChange={(open) => { if (!open) setCloseRoomId(null) }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10">
              <div className="flex justify-center py-3">
                <div className="w-10 h-1.5 rounded-full bg-foreground/20" />
              </div>
              <div className="px-6 pb-8 safe-area-bottom text-center">
                <Drawer.Title className="font-display text-2xl font-semibold text-foreground mb-2">關閉房間？</Drawer.Title>
                <p className="text-sm text-muted-foreground mb-6">房間 #{closeRoomId} 將在當前手牌結束後關閉</p>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setCloseRoomId(null)} className="h-14 glass-card rounded-2xl font-semibold text-foreground">取消</button>
                  <button onClick={() => setCloseRoomId(null)} className="h-14 bg-rose-500 rounded-2xl font-bold text-white">確認關閉</button>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* Pause Room Confirmation */}
      <Drawer.Root open={pauseConfirmRoom !== null} onOpenChange={(open) => { if (!open) setPauseConfirmRoom(null) }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10">
              <div className="flex justify-center py-3">
                <div className="w-10 h-1.5 rounded-full bg-foreground/20" />
              </div>
              <div className="px-6 pb-8 safe-area-bottom text-center">
                <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                  <Pause className="w-7 h-7 text-amber-400" />
                </div>
                <Drawer.Title className="font-display text-2xl font-semibold text-foreground mb-2">暫停房間</Drawer.Title>
                <p className="text-sm text-muted-foreground mb-6">暫停後新玩家將無法加入。目前進行中的牌局不受影響。</p>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setPauseConfirmRoom(null)} className="h-14 glass-card rounded-2xl font-semibold text-foreground">取消</button>
                  <button onClick={() => setPauseConfirmRoom(null)} className="h-14 bg-amber-500 rounded-2xl font-bold text-white">確認暫停</button>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </motion.div>
  )
}
